import { useEffect, useRef } from 'react';
import { useVideoManager } from '@/hooks/tgs2025/potionmaker/use-video-manager';

interface VideoOverlayProps {
  className?: string;
  zIndex?: number;
}

export const VideoOverlay = ({ className = '', zIndex = 50 }: VideoOverlayProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { 
    currentVideo,
    currentTrigger,
    isPlaying, 
    isLooping,
    onVideoEnded, 
    onVideoLoaded,
    preloadAllVideos 
  } = useVideoManager();

  // Preload all videos when component mounts
  useEffect(() => {
    preloadAllVideos();
  }, [preloadAllVideos]);

  // Handle video playback and looping logic
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentVideo || !currentTrigger) return;

    const handleLoadedData = () => {
      onVideoLoaded(currentVideo);
      if (isPlaying) {
        video.play().catch(console.error);
      }
    };

    const handleEnded = () => {
      // Check if this is a looped video that needs special handling
      if (currentTrigger.type === 'looped' && !isLooping) {
        const { loopLastSeconds, loopDuration } = currentTrigger;
        
        console.log(`Starting loop of last ${loopLastSeconds} seconds for ${loopDuration} seconds`);
        console.log(`Video duration: ${video.duration}`);
        
        // Set the video to start looping from the last N seconds
        const loopStartTime = Math.max(0, video.duration - loopLastSeconds);
        console.log(`Loop will play from ${loopStartTime} to ${video.duration}`);
        
        video.currentTime = loopStartTime;
        video.play().catch(console.error);
        
        // Track that we're now in looping mode
        const { setLooping } = useVideoManager.getState();
        setLooping(true);
        
        // Stop the looping after the specified duration
        setTimeout(() => {
          console.log('Loop duration completed, ending video');
          onVideoEnded();
        }, loopDuration * 1000);
        
        return;
      }
      
      // For looping videos, don't end - let the HTML5 loop attribute handle it
      if (currentTrigger.type === 'looping') {
        console.log('Looping video ended, but should continue looping via HTML5 loop attribute');
        return;
      }
      
      // For regular videos or when loop is complete
      onVideoEnded();
    };

    const handleTimeUpdate = () => {
      // If we're in looping mode for a looped video, restart the loop section
      if (currentTrigger.type === 'looped' && isLooping) {
        const { loopLastSeconds } = currentTrigger;
        const loopStartTime = Math.max(0, video.duration - loopLastSeconds);
        
        // If we've reached the end of the video (with small buffer), restart the loop section
        if (video.currentTime >= video.duration - 0.05) {
          console.log(`Restarting loop: jumping from ${video.currentTime} back to ${loopStartTime}`);
          video.currentTime = loopStartTime;
        }
      }
    };

    const handleError = (e: Event) => {
      console.error('Video playback error:', e);
      onVideoEnded(); // Clean up on error
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('error', handleError);

    // Set video source
    video.src = currentVideo;

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('error', handleError);
    };
  }, [currentVideo, currentTrigger, isPlaying, isLooping, onVideoEnded, onVideoLoaded]);

  // Don't render if no video is playing
  if (!currentVideo || !isPlaying) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center pointer-events-none ${className}`}
      style={{ zIndex }}
    >
     
      {/* Video element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        muted={false} // Allow sound for potion effects
        playsInline
        preload="auto"
        loop={currentTrigger?.type === 'looping'} // Enable HTML5 seamless looping for looping videos
      />
    </div>
  );
};
